import {randomId} from '../common/ids.js';
import {PeerId, Restrictions} from './types/common.js';
import {DomainKeys, Peer, State} from './types/state.js';
import {DomainUri} from './types/domain-registry.js';
import {Draft, enableMapSet, produce} from 'immer';
import {HandleReturnType} from '../domain.js';
import {GatewayRequest, GwRequestId} from './types/pending-requests.js';
import {JoinedPeer} from './peers.js';

enableMapSet();

// state manipulation

export function emptyState(nodeId: string, signingKey?: string): State {
    const signatureKey = signingKey ?? randomId();
    return {
        ids: {nodeId, currentId: 1},
        signatureKey
    };
}

export function joinDomain(state: State, peerId: PeerId, domain: DomainKeys, restrictions?: Restrictions): State {
    return produce(state, (draft: Draft<State>) => {
        draft.peers![peerId][domain] = (restrictions ? {restrictions} : {}) as unknown as undefined;
        draft.domains = draft.domains || {};
        draft.domains![domain] = draft.domains![domain] || new Set<PeerId>();
        draft.domains![domain]!.add(peerId);
    });
}

export function leaveDomain(state: State, peerId: PeerId, domain: DomainKeys): State {
    return produce(state, (s: Draft<State>) => {
        if (s.peers) {

            delete s.peers![peerId][domain];
        }
        if (s.domains && s.domains[domain]) {
            s.domains[domain]!.delete(peerId)
        }
    });
}

export function inDomain(state: State, peerId: PeerId, domain: DomainKeys): boolean {
    const has = state.domains?.[domain]?.has(peerId);
    return !!has;
}

export function peerInDomain<D extends DomainKeys>(peer: Peer, domain: D): peer is JoinedPeer<D> {
    return !!peer[domain];
}

// requests

export function setGatewayRequest(state: State, gwRequestId: GwRequestId, request: GatewayRequest): State {
    if (gwRequestId) {
        return produce(state, (draft: Draft<State>) => {
            draft.gatewayRequests = draft.gatewayRequests || {}
            draft.gatewayRequests[gwRequestId] = request;
        });
    }
    return state;
}

export function gatewayRequest(state: State, requestId: GwRequestId): GatewayRequest | undefined {
    if (requestId) {
        return state.gatewayRequests?.[requestId];
    }
}

export function removeGatewayRequest(state: State, requestId: GwRequestId): { state: State, removed?: GatewayRequest } {
    return produce<{state: State, removed?: GatewayRequest}>({state}, (draft: Draft<{ state: State, removed?: GatewayRequest }>) => {
        if (draft.state.gatewayRequests) {
            draft.removed = draft.state.gatewayRequests[requestId];
            delete draft.state.gatewayRequests[requestId];
            if (Object.keys(draft.state.gatewayRequests).length === 0) {
                delete draft.state.gatewayRequests;
            }
        }
    });
}

// domain registry

export function domainForUri(state: State, domainUri: DomainUri) {
    return state.registeredDomains?.[domainUri]?.domain;
}

export function threadStateAndActions<S extends State>(state: S, ...functions: Array<(state: S) => HandleReturnType>): HandleReturnType {
    return functions.reduce(([cs, cas], fun) => {
        const [s, as]: HandleReturnType = fun(cs as S);
        return [s, cas.concat(as)];
    }, [state, []] as HandleReturnType);
}

